import { useRefreshTick } from "../lib/useAutoRefresh";
import { useEffect, useState } from "react";
import { api, apiErr } from "../lib/api";
import { toast } from "sonner";
import { money } from "../lib/format";
import { Landmark, Receipt, FileDown, Mail } from "lucide-react";
import CompanyBreadcrumb from "../components/CompanyBreadcrumb";
import CraRemittancePanel from "../components/CraRemittancePanel";
import { useClientPerms } from "../lib/useClientPerms";
import { ClosedNoticeDialog } from "../components/ClosedNoticeDialog";
import NextRemittance from "../components/NextRemittance";
import { useSort, SortBtn } from "../lib/sortable";

const STATUS_CLS = (status) =>
  status === "Filed" ? "border-emerald-500 bg-emerald-50 text-emerald-700"
    : status === "Overdue" ? "border-red-500 bg-red-50 text-red-700"
    : status === "Upcoming" ? "border-amber-500 bg-amber-50 text-amber-700"
    : "border-neutral-300 bg-neutral-50 text-muted-foreground";

const RA = {
  month: (m) => m.month, runs: (m) => m.runs || 0, employees: (m) => m.employees_paid || 0, income_tax: (m) => m.income_tax || 0,
  cpp_ee: (m) => m.cpp_employee || 0, cpp_er: (m) => m.cpp_employer || 0,
  ei_ee: (m) => m.ei_employee || 0, ei_er: (m) => m.ei_employer || 0, remittance: (m) => m.remittance || 0,
};

export default function Remittance() {
  const params = new URLSearchParams(window.location.search);
  const [companies, setCompanies] = useState([]);
  const [companyId, setCompanyId] = useState("");
  const remitEdit = useClientPerms().canEdit("remittance");
  const [year, setYear] = useState(() => {
    const py = Number(params.get("pyear"));
    return py || new Date().getFullYear();
  });
  const [data, setData] = useState(null);
  const [focusKey, setFocusKey] = useState(params.get("period") || "");

  const tick = useRefreshTick();
  useEffect(() => {
    const qs = new URLSearchParams(window.location.search).get("company");
    api.get("/companies").then((r) => {
      setCompanies(r.data);
      if (qs && r.data.some((c) => c.company_id === qs)) setCompanyId(qs);
      else if (r.data[0]) setCompanyId(r.data[0].company_id);
    });
  }, []);

  useEffect(() => {
    if (!companyId) return;
    api.get(`/remittance?company_id=${companyId}&year=${year}`).then((r) => setData(r.data)).catch(() => setData(null));
  }, [companyId, year, tick]);

  const loadData = () => {
    if (!companyId) return;
    api.get(`/remittance?company_id=${companyId}&year=${year}`).then((r) => setData(r.data)).catch(() => setData(null));
  };
  const closingMonth = 0;
  const [closeDlg, setCloseDlg] = useState(null);   // {companyId, year, month, label}
  const sendClosed = (m) => setCloseDlg({ companyId, year, month: m.month, label: `${m.month_name} ${year}` });
  const toggleFiled = async (m) => {
    if (m.status === "No remittance") return;
    try {
      await api.post(`/companies/${companyId}/remittance-filing`, { kind: "cra", year, period: m.period, filed: !m.filed });
      loadData();
      toast.success(!m.filed ? `${m.month_name} marked filed` : `${m.month_name} filing cleared`);
    } catch (e) { toast.error(apiErr(e)); }
  };

  const t = data?.total;
  const { sorted, sort, toggle } = useSort(data?.months || [], RA, "month", "asc");

  return (
    <div className="w-full">
      <CompanyBreadcrumb label="Remittance (PD7A)" />
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <h1 className="text-4xl font-black tracking-tight">Remittance Summary</h1>
          <p className="text-sm text-muted-foreground mt-1 font-mono uppercase tracking-widest">Form PD7A · Employer source deductions</p>
        </div>
        <div className="flex gap-2">
          <input data-testid="remit-year-input" type="number" value={year} onChange={(e) => setYear(Number(e.target.value))} className="w-24 border border-input bg-white px-3 py-2.5 text-sm text-right tabular-nums focus:outline-none focus:ring-2 focus:ring-black" />
          <a data-testid="pd7a-annual-pdf" href={companyId ? `${process.env.REACT_APP_BACKEND_URL}/api/remittance/${companyId}/pd7a.pdf?year=${year}` : undefined} target="_blank" rel="noreferrer" className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border transition-colors whitespace-nowrap ${companyId ? "border-black bg-white hover:bg-black hover:text-white" : "border-neutral-300 text-neutral-400 pointer-events-none"}`}>
            <FileDown className="h-4 w-4" /> Annual PD7A
          </a>
        </div>
      </div>

      <NextRemittance companyId={companyId} variant="card"
        onFocus={(key, yr) => { if (yr && yr !== year) setYear(yr); setFocusKey(""); setTimeout(() => setFocusKey(key), 0); }} />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <Card label="Income Tax" value={t ? money(t.income_tax) : "—"} accent="text-red-600" testid="remit-total-tax" />
        <Card label="CPP (ee + er)" value={t ? money(t.cpp_total) : "—"} accent="text-red-600" testid="remit-total-cpp" />
        <Card label="EI (ee + er)" value={t ? money(t.ei_total) : "—"} accent="text-red-600" testid="remit-total-ei" />
        <Card label="Total Remittance" value={t ? money(t.remittance) : "—"} accent="text-black" big testid="remit-total" icon={Landmark} />
      </div>

      <CraRemittancePanel companyId={companyId} year={year} focusPeriodKey={focusKey}
        monthly={{ months: data?.months || [], remitEdit, toggleFiled, sendClosed, closingMonth }} />
      {closeDlg && <ClosedNoticeDialog remittance={closeDlg} onClose={() => setCloseDlg(null)} onSent={loadData} />}
      {!data?.months?.length && <div className="px-5 py-10 text-center text-sm text-muted-foreground border border-border bg-white">No payroll runs in {year} for this company.</div>}
    </div>
  );
}

function Card({ label, value, accent, big, testid, icon: Icon }) {
  return (
    <div data-testid={testid} className={`border bg-white p-5 ${big ? "border-black" : "border-border"}`}>
      <div className="flex items-center justify-between">
        <span className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">{label}</span>
        {Icon && <Icon className="h-4 w-4" />}
      </div>
      <div className={`mt-2 ${big ? "text-3xl" : "text-2xl"} font-black tracking-tight tabular-nums ${accent || ""}`}>{value}</div>
    </div>
  );
}
